// Shared "Ownership" field group for the partner-brand CREATE wizard and the
// EDIT page (relink), so the two surfaces never drift — same rule as the logo /
// colors / contact groups.
//
// A brand belongs to ONE broker OR ONE brokerage, never two; none set is a
// valid, supported state ("ownerless" — a general referral partner). Mutual
// exclusivity is structural HERE (the owner is a single tagged value, so picking
// one side clears the others) and re-checked server-side by
// PartnerBrandAdminService.RequireExclusiveOwnership.
//
// 🔴 A BUYER TEAM IS NOT OFFERED HERE (D2a). A team's site is minted with the
// team by BuyerTeamService.CreateAsync — Phoenix-branded, slug t<n>, managed
// automatically — and the server refuses a create or relink that names a team.
// `PartnerBrandOwnerKind` keeps the "buyerTeam" member so `ownerFromBrand` can
// still DESCRIBE such a row (the edit surface is refused server-side anyway);
// it is simply not a choice.
//
// Changing the owner never touches the brand's slug — UpdatePartnerBrandRequest
// doesn't carry one, and already-issued /prequalify/{slug} links must stay
// stable.
//
// RE-PICKING KEEPS THE CURRENT OWNER. "Change broker" used to call
// onChange({kind, id: null}) immediately, which put the form in a half-selected
// state — a kind with no record — that ownerIds() faithfully serializes as
// BOTH-NULL, i.e. ownerless. Saving from there silently unowned the brand.
// The re-pick is now a LOCAL `changing` flag: the committed owner stays in
// `value` (and on screen) until a replacement is actually chosen, so an
// abandoned re-pick costs nothing. Switching KIND still clears, because the
// old record genuinely cannot carry over to the other side.
//
// The chip's X was removed with the same change: it duplicated "Change …"
// exactly, and once "Change" stopped blanking the owner, the X's only
// remaining behaviour would have been to recreate the defect. "No owner" is
// the affordance for making a site ownerless.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Label } from "@/design-system/components/ui";
import { CounterpartyPicker } from "@/components/portfolioAdmin/loanDetail/parties/CounterpartyPicker";
import { BrokeragePicker } from "@/components/brokerages/BrokeragePicker";
import { brokersApi } from "@/api/brokers";
import { brokeragesApi } from "@/api/brokerages";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import { brokerageKeys } from "@/hooks/brokerages/brokerageKeys";
import { getBrokerDisplayName } from "@/lib/brokerDisplay";

export type PartnerBrandOwnerKind = "none" | "broker" | "brokerage" | "buyerTeam";

/** The brand's owner as ONE value — the client-side exclusivity guarantee. */
export interface PartnerBrandOwner {
  kind: PartnerBrandOwnerKind;
  /** Null while a kind is selected but no record is chosen yet. */
  id: string | null;
  /** Known at launch-from-context; otherwise resolved for display below. */
  name: string | null;
}

export const OWNERLESS: PartnerBrandOwner = { kind: "none", id: null, name: null };

/** Read the owner off a brand DTO (or any {ownerBrokerId, ownerBrokerageId}). */
export function ownerFromBrand(brand: {
  ownerBrokerId?: string | null;
  ownerBrokerageId?: string | null;
  ownerBuyerTeamId?: string | null;
}): PartnerBrandOwner {
  if (brand.ownerBrokerId) return { kind: "broker", id: brand.ownerBrokerId, name: null };
  if (brand.ownerBrokerageId) return { kind: "brokerage", id: brand.ownerBrokerageId, name: null };
  if (brand.ownerBuyerTeamId)
    return { kind: "buyerTeam", id: brand.ownerBuyerTeamId, name: null };
  return OWNERLESS;
}

/** Project the owner back onto the two request fields (never both set). */
export function ownerIds(owner: PartnerBrandOwner): {
  ownerBrokerId: string | null;
  ownerBrokerageId: string | null;
  ownerBuyerTeamId: string | null;
} {
  return {
    ownerBrokerId: owner.kind === "broker" ? owner.id : null,
    ownerBrokerageId: owner.kind === "brokerage" ? owner.id : null,
    ownerBuyerTeamId: owner.kind === "buyerTeam" ? owner.id : null,
  };
}

const KIND_OPTIONS: { value: PartnerBrandOwnerKind; label: string; hint: string }[] = [
  { value: "none", label: "No owner", hint: "A general referral partner." },
  { value: "broker", label: "Broker", hint: "One broker's own site." },
  { value: "brokerage", label: "Brokerage", hint: "The firm's shared site." },
];

interface Props {
  value: PartnerBrandOwner;
  onChange: (next: PartnerBrandOwner) => void;
  disabled?: boolean;
}

export function PartnerBrandOwnerSection({ value, onChange, disabled }: Props) {
  // Re-pick in progress. Local on purpose: an abandoned re-pick must not be
  // able to reach the save payload, so it never touches `value`.
  const [changing, setChanging] = useState(false);
  const brokerId = value.kind === "broker" ? value.id : null;
  const brokerageId = value.kind === "brokerage" ? value.id : null;

  // Resolve the display name when the caller didn't already know it (the edit
  // page hydrates ids only). Both reads share the existing detail caches.
  const brokerLookup = useQuery({
    queryKey: brokerKeys.detail(brokerId ?? ""),
    queryFn: () => brokersApi.get(brokerId!),
    enabled: !!brokerId && !value.name,
  });
  const brokerageLookup = useQuery({
    queryKey: brokerageKeys.legacyDetail(brokerageId ?? ""),
    queryFn: () => brokeragesApi.get(brokerageId!),
    enabled: !!brokerageId && !value.name,
  });

  const resolvedName =
    value.name ??
    (brokerId && brokerLookup.data ? getBrokerDisplayName(brokerLookup.data) : null) ??
    (brokerageId && brokerageLookup.data ? brokerageLookup.data.name : null);

  const selectKind = (kind: PartnerBrandOwnerKind) => {
    if (kind === value.kind) return;
    // Switching sides clears the other side's selection — exclusivity by shape.
    // A broker record cannot stand in as a brokerage owner, so unlike a re-pick
    // there is nothing worth preserving here.
    setChanging(false);
    onChange({ kind, id: null, name: null });
  };

  /** Commit a picked record and end any re-pick. */
  const pick = (next: PartnerBrandOwner) => {
    setChanging(false);
    onChange(next);
  };

  const hasOwner = value.kind !== "none" && !!value.id;
  // Pick when there is nothing committed yet, or while re-picking over one.
  const showPicker = value.kind !== "none" && !disabled && (changing || !value.id);
  const ownerNoun =
    value.kind === "broker" ? "broker" : value.kind === "brokerage" ? "brokerage" : "buyer team";

  return (
    <section className="flex flex-col gap-space-3">
      <h2 className="font-display font-bold text-h3 m-0">Ownership</h2>
      <p className="text-caption text-gray-mid m-0">
        Who this prequalification site belongs to. A site can be owned by one
        broker or one brokerage — only one of the two. Leave it unowned for a
        general referral partner. A buyer team&rsquo;s site is created
        automatically with the team and is not chosen here.
      </p>

      <fieldset
        className="flex flex-col gap-space-2 m-0 p-0 border-0"
        disabled={disabled}
        data-testid="partner-brand-owner-kind"
      >
        <legend className="sr-only">Owner type</legend>
        {KIND_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-center gap-space-2 text-small">
            <input
              type="radio"
              name="partner-brand-owner-kind"
              value={opt.value}
              data-testid={`owner-kind-${opt.value}`}
              checked={value.kind === opt.value}
              onChange={() => selectKind(opt.value)}
            />
            <span>{opt.label}</span>
            <span className="text-caption text-gray-mid">{opt.hint}</span>
          </label>
        ))}
      </fieldset>

      {hasOwner && (
        <div className="flex flex-col gap-space-1">
          <Label>Owning {ownerNoun}</Label>
          <span
            className="inline-flex w-fit items-center gap-space-2 rounded-full border border-border-light bg-white-warm px-space-2 py-space-1 text-small text-onyx"
            data-testid="partner-brand-owner-chip"
          >
            <span className="font-semibold">{resolvedName ?? "Loading…"}</span>
          </span>
          {!disabled &&
            (changing ? (
              // The committed owner is still live at this point — say so, so an
              // abandoned re-pick doesn't read as "I have already lost it".
              <p className="text-caption text-gray-mid m-0">
                Still the owner until you choose a replacement.
              </p>
            ) : (
              <Button
                type="button"
                variant="text"
                size="sm"
                className="w-fit"
                onClick={() => setChanging(true)}
              >
                Change {ownerNoun}
              </Button>
            ))}
        </div>
      )}

      {showPicker && value.kind === "broker" && (
        <CounterpartyPicker
          counterpartyType="Broker"
          label={changing ? "Replacement broker" : "Owning broker"}
          value={null}
          onChange={(selected) =>
            selected && pick({ kind: "broker", id: selected.id, name: selected.name })
          }
        />
      )}

      {showPicker && value.kind === "brokerage" && (
        <div className="flex flex-col gap-space-1">
          <Label>{changing ? "Replacement brokerage" : "Owning brokerage"}</Label>
          <BrokeragePicker
            onSelect={(r) =>
              pick({ kind: "brokerage", id: r.brokerageId, name: r.brokerageName })
            }
          />
        </div>
      )}

      {showPicker && changing && (
        <Button
          type="button"
          variant="text"
          size="sm"
          className="w-fit"
          onClick={() => setChanging(false)}
        >
          Cancel — keep {resolvedName ?? `the current ${ownerNoun}`}
        </Button>
      )}
    </section>
  );
}
